"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.textCSS = exports.responsiveTextCSS = exports.getTextVariantStyles = void 0;
var _utils = require("@amzn/stencil-design-tokens/js/web/utils");
var _stencilReactThemeDefault = require("@amzn/stencil-react-theme-default");
var _responsive = require("../responsive");
var _utils2 = require("../utils");
var _constants = require("./constants");
var _interfaces = require("./interfaces");
var _utils3 = require("./utils");
// This has to be done outside the Text component because it is reused by Link.
const baseCSS = _ref => {
  let {
    color,
    backgroundColor,
    display,
    font = _constants.defaultProps.font,
    fontWeight,
    fontStyle,
    hyphens = _constants.defaultProps.hyphens,
    margin,
    textAlign,
    textDecoration,
    wordBreak
  } = _ref;
  return {
    color: (0, _utils2.cssProp)(_stencilReactThemeDefault.colorPropAdapter, color),
    display,
    backgroundColor: (0, _utils2.cssProp)(_stencilReactThemeDefault.colorPropAdapter, backgroundColor),
    // The font prop only accepts names from the theme so the selector
    // will always return a value, so it's safe to cast to string
    fontFamily: (0, _stencilReactThemeDefault.fontFamilyPropAdapter)(font),
    fontStyle: fontStyle === 'italic' ? (0, _utils.token)('text.style.italic') : fontStyle,
    fontWeight: (0, _utils2.cssProp)(_stencilReactThemeDefault.fontWeightPropAdapter, fontWeight),
    hyphens,
    ...(0, _utils2.cssValuesProp)(_stencilReactThemeDefault.spacePropAdapter, margin, 'margin'),
    textAlign,
    textDecoration: textDecoration,
    wordBreak,
    em: {
      fontStyle: !fontStyle || fontStyle === 'italic' ? (0, _utils.token)('text.style.italic') : fontStyle
    },
    strong: {
      fontWeight: (0, _utils2.cssProp)(_stencilReactThemeDefault.fontWeightPropAdapter, fontWeight) || (0, _utils.token)('text.weight.bold')
    }
  };
};
const getTextVariantStyles = function (variant) {
  let breakpoint = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'lg';
  // Variant names follow token names, with dashes instead of dots
  const tokenizedVariant = variant.replace('-', '.');
  return {
    fontSize: (0, _utils.token)("text.".concat(tokenizedVariant, ".size.breakpoint-").concat(breakpoint)),
    lineHeight: (0, _utils.token)("text.".concat(tokenizedVariant, ".lineHeight.breakpoint-").concat(breakpoint)),
    fontWeight: (0, _utils.token)("text.".concat(tokenizedVariant, ".weight.breakpoint-").concat(breakpoint)),
    ...(variant === _interfaces.TextVariant.BodyHint && {
      fontStyle: (0, _utils.token)("text.".concat(tokenizedVariant, ".style.breakpoint-").concat(breakpoint))
    })
  };
};
exports.getTextVariantStyles = getTextVariantStyles;
const textCSS = _ref2 => {
  let {
    font = _constants.defaultProps.font,
    fontSize,
    lineHeight,
    fontWeight,
    variant,
    ...props
  } = _ref2;
  const hasVariant = !!variant;
  const hasLineHeightProp = !!fontSize && !lineHeight || !!lineHeight;
  const hasFontSizeProp = !!fontSize;
  const hasFontWeightProp = !!fontWeight;
  let textStyles = {};
  if (hasVariant) {
    textStyles = getTextVariantStyles(variant);
  }
  if (hasLineHeightProp) {
    const calculatedLineHeight = (0, _utils3.getLineHeight)({
      lineHeight,
      fontSize
    });
    textStyles = {
      ...textStyles,
      ...(calculatedLineHeight && {
        lineHeight: calculatedLineHeight
      })
    };
  }
  if (hasFontSizeProp) {
    textStyles = {
      ...textStyles,
      fontSize: (0, _utils2.cssProp)(_stencilReactThemeDefault.fontSizePropAdapter, fontSize)
    };
  }
  if (hasFontWeightProp) {
    textStyles = {
      ...textStyles,
      fontWeight: (0, _utils2.cssProp)(_stencilReactThemeDefault.fontWeightPropAdapter, fontWeight)
    };
  }
  return {
    ...baseCSS({
      font,
      ...props
    }),
    ...textStyles
  };
};
exports.textCSS = textCSS;
const responsiveTextCSS = _ref3 => {
  let {
    font = _constants.defaultProps.font,
    fontSize,
    lineHeight,
    fontWeight,
    variant,
    ...props
  } = _ref3;
  const {
    breakpoint: breakpoints
  } = props.theme.values;

  // Maps theme breakpoints to new theme token names
  const breakpointMap = {
    s: 'sm',
    m: 'md',
    l: 'lg',
    xl: 'xl'
  };
  const mediaQueries = Object.keys(breakpoints).reduce((queries, bp) => {
    const breakpoint = bp;
    const mediaQueryKey = (0, _responsive.buildMediaQuery)(breakpoints[breakpoint], true);
    const hasLineHeightProp = !!fontSize && !lineHeight || !!lineHeight;
    const hasFontSizeProp = !!fontSize;
    const hasFontWeightProp = !!fontWeight;
    const hasVariant = !!variant;
    let textStyles = {};
    if (hasLineHeightProp) {
      const calculatedLineHeight = (0, _utils3.getLineHeight)({
        lineHeight,
        fontSize
      });
      textStyles = {
        ...textStyles,
        ...(calculatedLineHeight && {
          lineHeight: calculatedLineHeight
        })
      };
    }
    if (hasFontSizeProp) {
      textStyles = {
        ...textStyles,
        fontSize: (0, _utils2.cssProp)(_stencilReactThemeDefault.fontSizePropAdapter, fontSize)
      };
    }
    if (hasFontWeightProp) {
      textStyles = {
        ...textStyles,
        fontWeight: (0, _utils2.cssProp)(_stencilReactThemeDefault.fontWeightPropAdapter, fontWeight)
      };
    }
    return {
      ...queries,
      [mediaQueryKey]: {
        ...(hasVariant && getTextVariantStyles(variant, breakpointMap[breakpoint])),
        ...textStyles
      }
    };
  }, {});
  return {
    ...baseCSS({
      font,
      ...props
    }),
    ...mediaQueries
  };
};
exports.responsiveTextCSS = responsiveTextCSS;